// src/components/IncomingOrders/OrderProductsAndSummation/HandledByCell.jsx

import PropTypes from "prop-types";
import { useState } from "react";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  Avatar,
  IconButton,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  Typography,
} from "@mui/material";


const userPropType = PropTypes.shape({
  avatarUrl: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  username: PropTypes.string.isRequired,
});

const avatarSx = {
  width: 28,
  height: 28,
  fontSize: 10,
};

const defaultAvatarSx = {
  ...avatarSx,
  opacity: 0.5,
  border: "1px dashed",
};


const compareStrings = (first, second) => first?.toLowerCase() === second?.toLowerCase();

const findUserByUsername = (username, availableUsers = []) => {
  if (!username) return null;

  return availableUsers.find(
    (user) => compareStrings(user.username, username)
  );
};

const getUserInitials = (user) => {
  if (!user?.username) return "";

  const { name, username } = user;

  const text = name?.trim() || username.trim();

  return text
    .split(/[\s.@_()-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};


const HandledByAvatar = ({ user }) => {
  if (!user?.username) {
    return (
      <Avatar sx={defaultAvatarSx}>
        <PersonOutlineIcon fontSize="small" />
      </Avatar>
    );
  }

  const { username, avatarUrl } = user;

  const name = user.name?.trim();

  const title = Boolean(name)
    ? `${username} (${name})`
    : `${username}`;

  return (
    <Avatar
      title={title}
      src={avatarUrl}
      sx={avatarSx}
    />
  );
};


const HandledByCell = ({
  username,
  availableUsers = [],
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const selectedUser = findUserByUsername(username, availableUsers) ?? { username };

  const userInitials = getUserInitials(selectedUser);

  const handleSelectUser = (user) => {
    onChange?.(user.username);
    setAnchorEl(null);
  };

  return (
    <TableCell sx={{ whiteSpace: "nowrap" }}>
      <IconButton
        size="small"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <HandledByAvatar user={selectedUser} />
      </IconButton>

      <Typography
        component="span"
        variant="body2"
        sx={{ opacity: 0.4, fontSize: "inherit" }}
      >
        {userInitials}
      </Typography>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {availableUsers.map((user) => (
          <MenuItem
            key={user.userId}
            selected={
              user.userId === selectedUser.userId ||
              compareStrings(user.username, selectedUser.username)
            }
            onClick={() => handleSelectUser(user)}
          >
            <ListItemAvatar
              sx={{
                minWidth: "auto",
                mr: 2,
              }}
            >
              <Avatar
                src={user.avatarUrl}
                sx={avatarSx}
              />
            </ListItemAvatar>

            <ListItemText primary={user.name} secondary={user.username} />
          </MenuItem>
        ))}
      </Menu>
    </TableCell>
  );
};

HandledByCell.propTypes = {
  availableUsers: PropTypes.arrayOf(userPropType),
  onChange: PropTypes.func,
  username: PropTypes.string,
};

export default HandledByCell;
